import { useMemo } from 'react'

import { useI18n } from '@affiliate-cabinet/i18n'
import { getApiAdvertisers, useQuery } from '@affiliate-cabinet/rest'
import { OffersTypesComponents, ResponseErrorInterface } from '@affiliate-cabinet/types'

import { selectTranslationKeys } from '../../../../shared/translations/select.translationKeys'
import { unknownAdvertiser } from './constants'
import { advertisersTranslationKeys } from './translations/advertisersTranslationKeys'

export const useAdvertisers = ({ withNotSelectedAdvertiser = false, withUnknownAdvertiser = false }) => {
  const { i18n } = useI18n()
  const { data, error, isFetching, refetch } = useQuery<unknown, ResponseErrorInterface, OffersTypesComponents['schemas']['AdvertisersCollection']>(
    ['useAdvertisers'],
    () => getApiAdvertisers(),
    {
      staleTime: Number.POSITIVE_INFINITY,
    },
  )

  const preparedData = useMemo(() => {
    const firstElement = {
      'data-testid': `advertiser-item-not-selected`,
      id: 0,
      name: '',
      alias: '',
      description: '',
      links: [],
      logo: '',
      platforms: [],
      regions: [],
      restrictionGrade: 0,
      shortDescription: '',
      theme: [],
      translationKey: selectTranslationKeys.selectNotSelected,
      value: '',
      website: '',
    }

    const advertisers = (data?.data || []).map((advertiser) => ({
      ...advertiser,
      'data-testid': `advertiser-item-${advertiser.id}`,
      translationKey: '',
      value: advertiser.id.toString(),
    }))

    if (withUnknownAdvertiser) {
      advertisers.push(unknownAdvertiser)
    }

    let preparedAdvertisers = advertisers.map(({ translationKey, ...advertiser }) => ({
      ...advertiser,
      label: i18n.t(translationKey) || advertiser.name,
    }))

    if (withNotSelectedAdvertiser) {
      preparedAdvertisers = [
        {
          ...firstElement,
          'data-testid': firstElement['data-testid'],
          label: i18n.t(firstElement.translationKey),
          alias: firstElement.alias,
          value: firstElement.value,
        },
        ...preparedAdvertisers,
      ]
    }

    return preparedAdvertisers
  }, [data?.data, withUnknownAdvertiser, withNotSelectedAdvertiser, i18n])

  const advertisersLabels = useMemo(() => {
    const advertisers = data?.data
      ? [
          ...data.data,
          { id: unknownAdvertiser.value, name: i18n.t(advertisersTranslationKeys.advertiserXXX.replace('xxx', unknownAdvertiser.alias)) },
        ]
      : []

    return Object.fromEntries(advertisers.map((v) => [Number(v.id), v.name]))
  }, [data?.data, i18n])

  return {
    advertisersLabels,
    data: preparedData,
    error,
    loading: isFetching,
    refetch,
  }
}
